// Q&A Item Form Component (Dialog)
// Date: February 5, 2026
// Updated with new 2-column design for automated checking logic

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { PracticeProjectQA, PracticeProjectQuestion } from '../../types/practiceProjects';

interface QAItemFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    question_text: string;
    variable_name?: string;
    check: {
      expected_output?: any;
      check_expression?: string;
      hint_text?: string;
      solution_text?: string;
      correct_message: string;
      incorrect_message: string;
    };
    order_index: number;
  }) => void;
  qaItem?: PracticeProjectQA | PracticeProjectQuestion | null;
  nextOrderIndex: number;
  isLoading?: boolean;
}

export function QAItemForm({
  open,
  onOpenChange,
  onSubmit,
  qaItem,
  nextOrderIndex,
  isLoading = false,
}: QAItemFormProps) {
  const [formData, setFormData] = useState({
    question_text: '',
    variable_name: '',
    expected_output: '',
    check_expression: '',
    hint_text: '',
    solution_text: '',
    correct_message: '',
    incorrect_message: '',
    order_index: nextOrderIndex,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when dialog opens or qaItem changes
  useEffect(() => {
    if (qaItem) {
      // Support new structure
      const isNewStructure = 'question_text' in qaItem;
      if (isNewStructure) {
        const question = qaItem as PracticeProjectQuestion;
        setFormData({
          question_text: question.question_text,
          variable_name: question.variable_name || '',
          expected_output: question.check?.expected_output 
            ? JSON.stringify(question.check.expected_output, null, 2) 
            : '',
          check_expression: question.check?.check_expression || '',
          hint_text: question.check?.hint_text || '',
          solution_text: question.check?.solution_text || '',
          correct_message: question.check?.correct_message || '',
          incorrect_message: question.check?.incorrect_message || '',
          order_index: question.order_index,
        });
      }
    } else {
      setFormData({
        question_text: '',
        variable_name: '',
        expected_output: '',
        check_expression: '',
        hint_text: '',
        solution_text: '',
        correct_message: '',
        incorrect_message: '',
        order_index: nextOrderIndex,
      });
    }
    setErrors({});
  }, [qaItem, nextOrderIndex, open]);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = () => {
    // Basic validation
    if (!formData.question_text.trim()) {
      setErrors({ question_text: 'Question text is required' });
      return;
    }

    // Parse expected_output if provided
    let parsedExpectedOutput: any = null;
    if (formData.expected_output.trim()) {
      try {
        parsedExpectedOutput = JSON.parse(formData.expected_output);
      } catch (error) {
        setErrors({ expected_output: 'Invalid JSON format' });
        return;
      }
    }

    // Build submission data
    const submitData = {
      question_text: formData.question_text,
      variable_name: formData.variable_name || undefined,
      check: {
        expected_output: parsedExpectedOutput,
        check_expression: formData.check_expression || undefined,
        hint_text: formData.hint_text || undefined,
        solution_text: formData.solution_text || undefined,
        correct_message: formData.correct_message || 'Correct! Well done.',
        incorrect_message: formData.incorrect_message || 'Incorrect. Try again.',
      },
      order_index: formData.order_index,
    };

    onSubmit(submitData);
  };

  const isEditMode = !!qaItem;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-5xl sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Question' : 'Add Question'}</DialogTitle>
          <DialogDescription>
            Configure automated checking logic
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="question_text">
              Question Text <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="question_text"
              placeholder="e.g., Load the dataset and assign the cleaned column names to a list..."
              value={formData.question_text}
              onChange={(e) => handleChange('question_text', e.target.value)}
              rows={3}
              className={errors.question_text ? 'border-destructive' : ''}
            />
            {errors.question_text && (
              <p className="text-xs text-destructive">{errors.question_text}</p>
            )}
            <p className="text-xs text-muted-foreground">
              This text appears in the notebook for the student.
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Validation Logic */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-sm font-medium">
                  1
                </div>
                <h3 className="font-semibold text-base">Validation Logic</h3>
              </div>

              {/* Target Python Variable */}
              <div className="space-y-2">
                <Label htmlFor="variable_name">Target Python Variable</Label>
                <Input
                  id="variable_name"
                  placeholder="e.g., cleaned_columns"
                  value={formData.variable_name}
                  onChange={(e) => handleChange('variable_name', e.target.value)}
                  className={errors.variable_name ? 'border-destructive' : ''}
                />
                {errors.variable_name && (
                  <p className="text-xs text-destructive">{errors.variable_name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  The variable name the student must define.
                </p>
              </div>

              {/* Expected Value (JSON) */}
              <div className="space-y-2">
                <Label htmlFor="expected_output">Expected Value (JSON)</Label>
                <Textarea
                  id="expected_output"
                  placeholder='["loan_id", "education", ...]'
                  value={formData.expected_output}
                  onChange={(e) => handleChange('expected_output', e.target.value)}
                  rows={4}
                  className={errors.expected_output ? 'border-destructive font-mono text-sm' : 'font-mono text-sm'}
                />
                {errors.expected_output && (
                  <p className="text-xs text-destructive">{errors.expected_output}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Ground truth for comparison. Must be valid JSON.
                </p>
              </div>

              {/* Advanced Check Expression */}
              <div className="space-y-2">
                <Label htmlFor="check_expression">
                  Advanced Check Expression <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <Textarea
                  id="check_expression"
                  placeholder="e.g., result > 0 and result < 100"
                  value={formData.check_expression}
                  onChange={(e) => handleChange('check_expression', e.target.value)}
                  rows={3}
                  className={errors.check_expression ? 'border-destructive font-mono text-sm' : 'font-mono text-sm'}
                />
                {errors.check_expression && (
                  <p className="text-xs text-destructive">{errors.check_expression}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Python expression for complex validation logic.
                </p>
              </div>
            </div>

            {/* Right Column: Feedback & Support */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-sm font-medium">
                  2
                </div>
                <h3 className="font-semibold text-base">Feedback & Support</h3>
              </div>

              {/* Hint Text */}
              <div className="space-y-2">
                <Label htmlFor="hint_text">Hint Text</Label>
                <Textarea
                  id="hint_text"
                  placeholder="e.g., Use df.columns.str.strip()..."
                  value={formData.hint_text}
                  onChange={(e) => handleChange('hint_text', e.target.value)}
                  rows={3}
                  className={errors.hint_text ? 'border-destructive' : ''}
                />
                {errors.hint_text && (
                  <p className="text-xs text-destructive">{errors.hint_text}</p>
                )}
              </div>

              {/* Solution Code */}
              <div className="space-y-2">
                <Label htmlFor="solution_text">Solution Code</Label>
                <Textarea
                  id="solution_text"
                  placeholder="df.columns = df.columns.str.strip()..."
                  value={formData.solution_text}
                  onChange={(e) => handleChange('solution_text', e.target.value)}
                  rows={4}
                  className={errors.solution_text ? 'border-destructive font-mono text-sm' : 'font-mono text-sm'}
                />
                {errors.solution_text && (
                  <p className="text-xs text-destructive">{errors.solution_text}</p>
                )}
              </div>

              {/* Success & Failure Messages */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="correct_message" className="text-green-600">Success Message</Label>
                  <Input
                    id="correct_message"
                    placeholder="Correct! Well done."
                    value={formData.correct_message}
                    onChange={(e) => handleChange('correct_message', e.target.value)}
                    className={errors.correct_message ? 'border-destructive' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="incorrect_message" className="text-red-600">Failure Message</Label>
                  <Input
                    id="incorrect_message"
                    placeholder="Not quite. Make sure you..."
                    value={formData.incorrect_message}
                    onChange={(e) => handleChange('incorrect_message', e.target.value)}
                    className={errors.incorrect_message ? 'border-destructive' : ''}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Index (hidden for simplicity, auto-managed) */}
          <input type="hidden" value={formData.order_index} />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Saving...' : isEditMode ? 'Update Question' : 'Add Question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
